import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { siteInfo, testimonials } from '@/mocks/content';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9u531d3pcjqs2fcsmag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '';
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
    setTimeout(() => setFormStatus('idle'), 4000);
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <nav className="bg-background-50 border-b border-background-200/70">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500">
              <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
              <li><div className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line text-[10px]"></i></div></li>
              <li className="text-foreground-700 font-medium">关于我们</li>
            </ol>
          </div>
        </nav>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[900px] mx-auto px-4 md:px-8">
            <div className="text-center mb-14">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                关于我们
              </h1>
              <p className="text-foreground-600 max-w-[600px] mx-auto leading-relaxed">
                {siteInfo.name} — {siteInfo.tagline}
              </p>
            </div>

            <div className="space-y-10 text-sm text-foreground-700 leading-relaxed">
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">品牌故事</h2>
                <p>
                  {siteInfo.name}创立于{siteInfo.founded}，由一群热爱日韩影视文化的追剧达人共同打造。我们深知语言障碍给中文观众带来的困扰 — 明明有好剧却找不到中文字幕，找到了字幕却不知道哪个平台的翻译质量更好。
                </p>
                <p className="mt-3">
                  正是基于这样的痛点，我们决定创建一个专注于<strong>中文字幕日韩影视资源</strong>的导航平台。我们不生产内容，而是帮助用户<strong>找到最好、最快的中文字幕资源</strong>。从日韩剧到电影，从热门综艺到小众佳作，我们希望每一位中文用户都能轻松享受日韩影视的魅力。
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">我们的优势</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { icon: 'ri-speed-up-line', title: '更新速度对比', desc: '实时追踪各大平台的字幕更新速度，帮你选择最快的追剧路径' },
                    { icon: 'ri-translate-2', title: '字幕质量评测', desc: '多维度对比各平台中文字幕质量，让你看到最好的翻译版本' },
                    { icon: 'ri-guide-line', title: '平台导航指引', desc: '一站整合Netflix、KKTV等主流平台的日韩内容，告别盲目搜索' },
                  ].map((item) => (
                    <div key={item.title} className="rounded-xl bg-background-100 p-5">
                      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-3">
                        <i className={`${item.icon} text-base`}></i>
                      </div>
                      <h3 className="font-heading font-semibold text-foreground-950 mb-2 text-sm">{item.title}</h3>
                      <p className="text-xs text-foreground-600">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">用户口碑</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {testimonials.map((t) => (
                    <div key={t.name} className="rounded-xl bg-background-100 p-5">
                      <div className="flex items-center gap-3 mb-3">
                        <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                        <div>
                          <div className="text-sm font-semibold text-foreground-900">{t.name}</div>
                          <div className="text-xs text-foreground-500">{t.role}</div>
                        </div>
                      </div>
                      <p className="text-xs text-foreground-600 leading-relaxed mb-3">&ldquo;{t.content}&rdquo;</p>
                      <div className="flex items-center gap-0.5">
                        {Array.from({ length: t.rating }).map((_, i) => (
                          <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-star-fill text-accent-500 text-xs"></i>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground-950 mb-4">联系我们</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                        <i className="ri-mail-line"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">电子邮件</div>
                        <div className="text-xs text-foreground-600 mt-0.5">{siteInfo.email}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                        <i className="ri-wechat-line"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">微信公众号</div>
                        <div className="text-xs text-foreground-600 mt-0.5">{siteInfo.wechat}</div>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg bg-primary-100 text-primary-500">
                        <i className="ri-map-pin-line"></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground-900">地址</div>
                        <div className="text-xs text-foreground-600 mt-0.5">{siteInfo.address}</div>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl bg-background-100 p-5">
                    <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-4">发送消息</h3>
                    <form onSubmit={handleSubmit} data-readdy-form className="space-y-3">
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="您的姓名"
                          required
                          className="w-full px-3.5 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="您的邮箱"
                          required
                          className="w-full px-3.5 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          placeholder="请输入您的留言（500字以内）"
                          rows={4}
                          maxLength={500}
                          required
                          className="w-full px-3.5 py-2.5 rounded-lg bg-background-50 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                        ></textarea>
                      </div>
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                        className="honeypot-field"
                      />
                      <button
                        type="submit"
                        className="whitespace-nowrap w-full py-2.5 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                      >
                        发送消息
                      </button>
                      {formStatus === 'success' && (
                        <p className="text-xs text-green-600 text-center">消息已发送，我们会尽快回复您！</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-xs text-red-500 text-center">{formError}</p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}